import React, { Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown } from 'lucide-react';

const HeroScene = lazy(() => import('./HeroScene'));

const FALLBACK = 'https://images.unsplash.com/photo-1677311573471-fcd492a8b2d9';

export default function Hero({ onNav, reduced }) {
  return (
    <section
      id="hero"
      className="relative h-screen w-full flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {reduced ? (
        <img
          src={FALLBACK}
          alt="Abstract refractive glass form"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
      ) : (
        <Suspense fallback={<div className="absolute inset-0 bg-bone" />}>
          <HeroScene />
        </Suspense>
      )}

      <div className="relative z-10 text-center px-6 pointer-events-none">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="font-sans text-xs uppercase tracking-[0.4em] text-obsidian/60 mb-6"
        >
          Digital solutions studio
        </motion.div>

        <h1
          className="serif tracking-tighter leading-[0.9] text-[13vw] md:text-[9vw]"
          style={{ textShadow: '0 1px 30px rgba(244,244,240,0.85), 0 0 60px rgba(244,244,240,0.55)' }}
        >
          {['Digital', 'products that', 'move your business'].map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: '110%' }}
                animate={{ y: 0 }}
                transition={{ delay: 0.4 + i * 0.12, duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
              >
                {i === 1 ? (
                  <>
                    products <span className="italic">that</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="font-sans text-base md:text-lg text-graphite max-w-xl mx-auto mt-8"
        >
          Custom SaaS, interactive 3D websites, mobile apps & digital marketing —
          built fast for small and medium businesses.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 pointer-events-auto"
        >
          <button
            onClick={() => onNav('contact')}
            className="px-10 py-4 rounded-full bg-obsidian text-bone font-sans text-xs uppercase tracking-[0.2em] hover:bg-signal transition-colors duration-500"
            data-testid="hero-start-project"
          >
            Start a Project
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-obsidian/50"
      >
        <span className="font-sans text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
}
